import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ContactType } from "@/enum/ContactType";
import {
  useCreateContactMutation,
  useAddContactsToListMutation,
  useBulkAddTagMutation,
} from "@/store/rest_api";
import { toast } from "sonner";
import ContactForm, { ContactFormData } from "@/components/pages/contacts/ContactForm";

export default function CreateContactPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isLead = searchParams.get("type") === ContactType.LEAD;
  const [createContact, { isLoading }] = useCreateContactMutation();
  const [addToList] = useAddContactsToListMutation();
  const [bulkAddTag] = useBulkAddTagMutation();

  const handleSubmit = async (data: ContactFormData) => {
    try {
      // 1) create contact
      const res = await createContact(data).unwrap();
      const contact = (res as any)?.data ?? res; // supports either {data: Contact} or Contact
      const contactId: string = contact.id;

      // 2) optionally add to lists (reuse existing endpoint)
      const listIds = data.listIds ?? [];
      if (listIds.length > 0) {
        await Promise.all(
            listIds.map((listId) =>
                addToList({ listId, contactIds: [contactId] }).unwrap()
            )
        );
      }

      // 3) optionally add tags
      const tagIds = data.tagIds ?? [];
      if (tagIds.length > 0) {
        await Promise.all(
            tagIds.map((tagId) =>
                bulkAddTag({ tagId, contactIds: [contactId] }).unwrap()
            )
        );
      }

      toast.success(isLead ? "Lead created" : "Contact created");
      navigate(`/contacts/${contactId}/edit`);
    } catch (e: any) {
      toast.error(e?.detail ?? (isLead ? "Failed to create lead" : "Failed to create contact"));
    }
  };

  return (
      <div className="space-y-6 p-4 max-w-3xl mx-auto">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{isLead ? "Create Lead" : "Create Contact"}</h1>
          <p className="text-muted-foreground">
            {isLead ? "Add a new lead to your organisation" : "Add a new contact to your organisation"}
          </p>
        </div>

        <ContactForm
          onSubmit={handleSubmit}
          isSubmitting={isLoading}
          submitLabel={isLead ? "Create Lead" : "Create Contact"}
          defaultType={isLead ? ContactType.LEAD : undefined}
        />
      </div>
  );
}